import { useEffect, useRef } from 'react'

import { PdfViewerEvent } from '../../PdfViewer.constants'
import {
  getPdfViewerSelectionAreaBoxInitValues,
  PDF_VIEWER_SELECTION_AREA_BOX_MAX_HEIGHT_RATIO,
} from '../../PdfViewerSelectionAreaBox'

import { transformSelectAreaCoordinatesToPercentCoordinates } from './usePdfViewerSelectionAreaBox.utils'

export function usePdfViewerSelectionAreaBoxPageSync({
  currentPageRef,
  getCurrentPageRect,
  isInitSelectAreaRef,
  maxHeight,
  maxWidth,
  pageRectRef,
  pdfViewerInstance,
  percentCoordinatesRef,
  selectAreaLayerRef,
  selectBoxRef,
  setCoordinatesToSelectBoxStyle,
  setPdfPageCoordinates,
  updateBoxFromRelativePdfCoordinates,
  onPageSync,
}) {
  const maxResizeWidthRef = useRef(maxWidth || 0)
  const maxResizeHeightRef = useRef(maxHeight || 0)

  useEffect(() => {
    if (pdfViewerInstance) {
      const { eventBus, pdfViewer, selectAreaLayer } = pdfViewerInstance
      const abortController = new AbortController()
      const { signal } = abortController

      function syncSelectAreaWithCurrentPage() {
        const currentPage = pdfViewer.currentPageNumber
        const pageContainer = pdfViewer.getPageView(currentPage - 1)?.div

        if (pageContainer && selectAreaLayer) {
          currentPageRef.current = pageContainer
          selectAreaLayerRef.current = selectAreaLayer

          const pdfDefaultRect = getCurrentPageRect()

          setPdfPageCoordinates(pdfDefaultRect)
          onPageSync(currentPage)

          pageRectRef.current = pdfDefaultRect

          if (!maxWidth) {
            maxResizeWidthRef.current = pdfDefaultRect.width
          }

          if (!maxHeight) {
            maxResizeHeightRef.current =
              pdfDefaultRect.height *
              PDF_VIEWER_SELECTION_AREA_BOX_MAX_HEIGHT_RATIO
          }

          if (selectBoxRef.current && !isInitSelectAreaRef.current) {
            const coordinates =
              getPdfViewerSelectionAreaBoxInitValues(pdfDefaultRect)

            percentCoordinatesRef.current =
              transformSelectAreaCoordinatesToPercentCoordinates(
                coordinates,
                pdfDefaultRect
              )

            setCoordinatesToSelectBoxStyle(coordinates)
          }

          updateBoxFromRelativePdfCoordinates()
        }
      }

      // a cached page re-attaches without 'pagerendered', so the switch is
      // re-synced a frame after it settles
      function handlePageChanging() {
        requestAnimationFrame(syncSelectAreaWithCurrentPage)
      }

      eventBus.on(
        PdfViewerEvent.ScaleChanging,
        updateBoxFromRelativePdfCoordinates,
        { signal }
      )
      eventBus.on(PdfViewerEvent.PageRendered, syncSelectAreaWithCurrentPage, {
        signal,
      })
      eventBus.on(PdfViewerEvent.PageChanging, handlePageChanging, { signal })

      return () => {
        abortController.abort()
      }
    }
  }, [pdfViewerInstance])

  return {
    maxResizeHeightRef,
    maxResizeWidthRef,
  }
}
